/**
 * Loading Button Component
 *
 * A wrapper that toggles between normal and loading text states within a server-rendered button.
 * Expects the button and text spans to be provided as children.
 *
 * @example
 * <gmx-loading-btn>
 *   <button class="gmx-btn gmx-btn-primary gmx-loading-btn">
 *     <span class="loading-btn-text">Load More</span>
 *     <span class="loading-btn-loading">Loading<span class="loading-btn-ellipsis"></span></span>
 *   </button>
 * </gmx-loading-btn>
 *
 * @example With loading state
 * <gmx-loading-btn data-loading>
 *   <button class="gmx-btn gmx-btn-secondary gmx-loading-btn">
 *     <span class="loading-btn-text">Submit</span>
 *     <span class="loading-btn-loading">Submitting<span class="loading-btn-ellipsis"></span></span>
 *   </button>
 * </gmx-loading-btn>
 */
export class GMXLoadingButton extends HTMLElement {
	private buttonEl: HTMLButtonElement | null = null

	static get observedAttributes(): string[] {
		return ['data-loading']
	}

	/**
	 * Whether the button is in loading state
	 */
	get loading(): boolean {
		return this.hasAttribute('data-loading')
	}
	set loading(val: boolean) {
		if (val) {
			this.setAttribute('data-loading', '')
		} else {
			this.removeAttribute('data-loading')
		}
	}

	connectedCallback(): void {
		this.buttonEl = this.querySelector('button')
		if (this.buttonEl) {
			this.setupButton()
		}
	}

	attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
		if (oldValue === newValue) return

		if (name === 'data-loading') {
			this.updateLoadingState()
		}
	}

	private setupButton(): void {
		if (!this.buttonEl) return

		// Sync initial loading state to button
		if (this.loading) {
			this.buttonEl.setAttribute('data-loading', '')
		}

		// Forward click events
		this.buttonEl.addEventListener('click', (e) => {
			if (this.loading) {
				e.preventDefault()
				e.stopPropagation()
				return
			}
			// Dispatch a custom event that bubbles
			this.dispatchEvent(new CustomEvent('loading-btn-click', {
				bubbles: true,
				composed: true,
				detail: { originalEvent: e }
			}))
		})
	}

	private updateLoadingState(): void {
		if (!this.buttonEl) return
		if (this.loading) {
			this.buttonEl.setAttribute('data-loading', '')
		} else {
			this.buttonEl.removeAttribute('data-loading')
		}
	}

	/**
	 * Start loading state programmatically
	 */
	startLoading(): void {
		this.loading = true
	}

	/**
	 * Stop loading state programmatically
	 */
	stopLoading(): void {
		this.loading = false
	}
}

if (!customElements.get('gmx-loading-btn')) {
	customElements.define('gmx-loading-btn', GMXLoadingButton)
}

